import { API } from "../api";

export const authApi = API.injectEndpoints({
  endpoints: (builder) => ({
    userLogin: builder.mutation({
      query: (body) => ({
        url: "/onboard/admins/login",
        method: "POST",
        body,
      }),
    }),
    getCoop: builder.query({
      query: ({ domain_url }) => ({
        url: `/onboard/getCooperative?domain_url=${domain_url}`,
        method: "GET",
      }),
    }),
    getCurrentUserProfile: builder.query({
      query: (body) => ({
        url: "/cooperatives/profile?guard=staff",
        method: "GET",
        body,
      }),
    }),
    getDashboard: builder.query({
      query: (body) => ({
        url: "/admins/dashboard",
        method: "GET",
        body,
      }),
    }),
    getCooperativeRegistrationApplications: builder.query({
      query: ({ page = 1, status = "pending" } = {}) => ({
        url: "/admins/cooperative-registration-applications",
        method: "GET",
        params: { page, status },
      }),
    }),
    getDemoRequests: builder.query({
      query: ({ page = 1, status = "PENDING" } = {}) => ({
        url: "/admins/demo-requests",
        method: "GET",
        params: { page, status },
      }),
    }),
    getContactSubmissions: builder.query({
      query: ({ page = 1, status = "PENDING" } = {}) => ({
        url: "/admins/contact-submissions",
        method: "GET",
        params: { page, status },
      }),
    }),
    getUserSubscriptions: builder.query({
      query: (body) => ({
        url: "/admins/subscriptions",
        method: "GET",
        body,
      }),
    }),
    getCooperatives: builder.query({
      query: (arg) => {
        const id = typeof arg === "object" && arg != null ? arg.id : arg;
        const branchId =
          typeof arg === "object" && arg != null ? arg.branchId : undefined;
        let url = `/admins/cooperatives/${id}/show`;
        if (branchId) {
          url += `?branch_id=${branchId}`;
        }
        return {
          url,
          method: "GET",
        };
      },
    }),
    updateCooperative: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/admins/cooperatives/${id}/update`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "Cooperatives", id: arg.id },
      ],
    }),
    getCooperativeInvestorCapabilities: builder.query({
      query: (companyId) => ({
        url: `/admins/cooperatives/${companyId}/investor-capabilities`,
        method: "GET",
      }),
      transformResponse: (res) => res?.data ?? res,
      providesTags: (result, error, companyId) => [
        { type: "CooperativeInvestorCapabilities", id: companyId },
      ],
    }),
    updateCooperativeInvestorCapabilities: builder.mutation({
      query: ({ companyId, ...body }) => ({
        url: `/admins/cooperatives/${companyId}/investor-capabilities`,
        method: "PATCH",
        body,
      }),
      transformResponse: (res) => res?.data ?? res,
      invalidatesTags: (result, error, arg) => [
        { type: "CooperativeInvestorCapabilities", id: arg.companyId },
      ],
    }),
    getCooperativeModuleAssignments: builder.query({
      query: (companyId) => ({
        url: `/admins/cooperatives/${companyId}/module-assignments`,
        method: "GET",
      }),
      transformResponse: (res) => res?.data ?? res,
      providesTags: (result, error, companyId) => [
        { type: "CooperativeModuleAssignments", id: companyId },
      ],
    }),
    updateCooperativeModuleAssignments: builder.mutation({
      query: ({ companyId, modules }) => ({
        url: `/admins/cooperatives/${companyId}/module-assignments`,
        method: "PUT",
        body: { modules },
      }),
      transformResponse: (res) => res?.data ?? res,
      invalidatesTags: (result, error, arg) => [
        { type: "CooperativeModuleAssignments", id: arg.companyId },
        { type: "CooperativeInvestorCapabilities", id: arg.companyId },
      ],
    }),
    resetCooperativeModuleAssignments: builder.mutation({
      query: (companyId) => ({
        url: `/admins/cooperatives/${companyId}/module-assignments/reset-to-plan`,
        method: "POST",
      }),
      transformResponse: (res) => res?.data ?? res,
      invalidatesTags: (result, error, companyId) => [
        { type: "CooperativeModuleAssignments", id: companyId },
        { type: "CooperativeInvestorCapabilities", id: companyId },
      ],
    }),
    getCooperativePaymentSettings: builder.query({
      query: (companyId) => ({
        url: `/admins/cooperatives/${companyId}/payment-settings`,
        method: "GET",
      }),
      transformResponse: (res) => res?.data ?? res,
      providesTags: (result, error, companyId) => [
        { type: "CooperativePaymentSettings", id: companyId },
      ],
    }),
    updateCooperativePaymentSettings: builder.mutation({
      query: ({ companyId, ...body }) => ({
        url: `/admins/cooperatives/${companyId}/payment-settings`,
        method: "PATCH",
        body,
      }),
      transformResponse: (res) => res?.data ?? res,
      invalidatesTags: (result, error, arg) => [
        { type: "CooperativePaymentSettings", id: arg.companyId },
      ],
    }),
    publishWebsiteRouting: builder.mutation({
      query: (id) => ({
        url: `/admins/cooperatives/${id}/publish-website-routing`,
        method: "POST",
      }),
    }),
    getAllCooperatives: builder.query({
      query: (body) => ({
        url: "/admins/cooperatives",
        method: "GET",
        body,
      }),
    }),
    getSubscriptions: builder.query({
      query: (body) => ({
        url: "/admins/subscriptions",
        method: "GET",
        body,
      }),
      providesTags: ["Subscriptions"],
    }),
    getSubscription: builder.query({
      query: (id) => ({
        url: `/admins/subscriptions/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{ type: "Subscriptions", id }],
    }),
    createSubscription: builder.mutation({
      query: (body) => ({
        url: "/admins/subscriptions/create",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Subscriptions"],
    }),
    updateSubscription: builder.mutation({
      query: (body) => ({
        url: "/admins/subscriptions/update",
        method: "PATCH",
        body,
      }),
      invalidatesTags: (result, error, arg) => [
        "Subscriptions",
        { type: "Subscriptions", id: arg?.id },
      ],
    }),
    getModules: builder.query({
      query: (params = {}) => ({
        url: "/admins/modules",
        method: "GET",
        params: {
          all: 1,
          per_page: 200,
          ...params,
        },
      }),
      providesTags: ["Modules"],
    }),
    getActiveCooperativesSubscriptions: builder.query({
      query: (body) => ({
        url: "/admins/cooperatives/active",
        method: "GET",
        body,
      }),
    }),
    createCooperative: builder.mutation({
      query: (body) => ({
        url: "/admins/cooperatives/create",
        method: "POST",
        body,
      }),
    }),
    updateCooperativeSubscription: builder.mutation({
      query: (body) => ({
        url: "/admins/cooperatives/subscribe",
        method: "PUT",
        body,
      }),
    }),
    getCooperativeLoans: builder.query({
      query: ({ companyId, page = 1, perPage = 20, branchId }) => {
        let url = `/admins/cooperatives/${companyId}/loans?page=${page}&per_page=${perPage}`;
        if (branchId) url += `&branch_id=${branchId}`;
        return {
          url,
          method: "GET",
        };
      },
    }),
    getCooperativeContributions: builder.query({
      query: ({ companyId, page = 1, perPage = 20, branchId }) => {
        let url = `/admins/cooperatives/${companyId}/contributions?page=${page}&per_page=${perPage}`;
        if (branchId) url += `&branch_id=${branchId}`;
        return {
          url,
          method: "GET",
        };
      },
    }),
    getCooperativeTransactions: builder.query({
      query: ({ companyId, page = 1, perPage = 20, branchId }) => {
        let url = `/admins/cooperatives/${companyId}/transactions?page=${page}&per_page=${perPage}`;
        if (branchId) url += `&branch_id=${branchId}`;
        return {
          url,
          method: "GET",
        };
      },
    }),
    getCooperativeStatistics: builder.query({
      query: ({ companyId, filter = "weekly", branchId }) => {
        let url = `/admins/cooperatives/${companyId}/statistics?filter=${filter}`;
        if (branchId) url += `&branch_id=${branchId}`;
        return {
          url,
          method: "GET",
        };
      },
    }),
    sendCooperativeCredentials: builder.mutation({
      query: (companyId) => ({
        url: `/admins/cooperatives/${companyId}/send-credentials`,
        method: "POST",
        body: { companyId },
      }),
    }),
    resendWelcomeEmail: builder.mutation({
      query: (companyId) => ({
        url: `/admins/cooperatives/${companyId}/resend-welcome`,
        method: "POST",
        body: { companyId },
      }),
    }),
    startCooperativeSupportSession: builder.mutation({
      query: ({ companyId, branchId }) => ({
        url: `/admins/cooperatives/${companyId}/support-session`,
        method: "POST",
        body: branchId ? { branch_id: branchId } : {},
      }),
      transformResponse: (res) => res?.data ?? res,
    }),
    prorateMemberSlots: builder.query({
      query: ({ companyId, new_slot_count, discount_percentage }) => {
        const params = new URLSearchParams({ new_slot_count });
        if (discount_percentage != null && discount_percentage !== "")
          params.set("discount_percentage", discount_percentage);
        return {
          url: `/admins/cooperatives/${companyId}/member-slots/prorate?${params.toString()}`,
          method: "GET",
        };
      },
      transformResponse: (res) => res?.data ?? res,
    }),
    topUpMemberSlots: builder.mutation({
      query: ({ companyId, new_slot_count, discount_percentage }) => ({
        url: `/admins/cooperatives/${companyId}/member-slots/top-up`,
        method: "POST",
        body: { new_slot_count, discount_percentage: discount_percentage ?? 0 },
      }),
      invalidatesTags: (result, error, arg) => [{ type: "Cooperatives", id: arg.companyId }],
    }),
    checkDomainAvailability: builder.mutation({
      query: (domain) => ({
        url: "/admins/cooperatives/check-domain",
        method: "POST",
        body: { domain },
      }),
    }),
    checkEmailAvailability: builder.mutation({
      query: (email) => ({
        url: "/admins/cooperatives/check-email",
        method: "POST",
        body: { email },
      }),
    }),
    getExpiringCooperativesSubscriptions: builder.query({
      query: (body) => ({
        url: "/admins/cooperatives/expiring",
        method: "GET",
        body,
      }),
    }),
    getExpiredCooperativesSubscriptions: builder.query({
      query: (body) => ({
        url: "/admins/cooperatives/expired",
        method: "GET",
        body,
      }),
    }),
    getTransactionHistory: builder.query({
      query: (params = {}) => {
        const { page, per_page, company_id, date_from, date_to, product_type, event_type } = params;
        const searchParams = new URLSearchParams();
        if (page != null) searchParams.set("page", page);
        if (per_page != null) searchParams.set("per_page", per_page);
        if (company_id != null) searchParams.set("company_id", company_id);
        if (date_from) searchParams.set("date_from", date_from);
        if (date_to) searchParams.set("date_to", date_to);
        if (product_type) searchParams.set("product_type", product_type);
        if (event_type) searchParams.set("event_type", event_type);
        const qs = searchParams.toString();
        return {
          url: `/admins/transaction-history${qs ? `?${qs}` : ""}`,
          method: "GET",
        };
      },
      providesTags: (result, error, arg) => ["TransactionHistory"],
    }),
    // Module endpoints
    createModule: builder.mutation({
      query: (body) => ({
        url: "/admins/modules/create",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Modules"],
    }),
    updateModule: builder.mutation({
      query: (body) => ({
        url: "/admins/modules/update",
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["Modules"],
    }),
    // Sub-module endpoints
    getSubModules: builder.query({
      query: (params = {}) => ({
        url: "/admins/sub-modules",
        method: "GET",
        params,
      }),
      providesTags: ["SubModules"],
    }),
    createSubModule: builder.mutation({
      query: (body) => ({
        url: "/admins/sub-modules/create",
        method: "POST",
        body,
      }),
      invalidatesTags: ["SubModules", "Modules"],
      transformErrorResponse: (response, meta, arg) => {
        console.log("createSubModule error response:", response);
        return response;
      },
    }),
    updateSubModule: builder.mutation({
      query: (body) => ({
        url: "/admins/sub-modules/update",
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["SubModules", "Modules"],
      transformErrorResponse: (response, meta, arg) => {
        console.log("updateSubModule error response:", response);
        return response;
      },
    }),
  }),
});

export const {
  useUserLoginMutation,
  useGetCoopQuery,
  useGetCurrentUserProfileQuery,
  useLazyGetCurrentMemberProfileQuery,
  useLazyGetCurrentUserProfileQuery,
  useGetUserSubscriptionsQuery,
  useGetCooperativesQuery,
  useGetAllCooperativesQuery,
  useUpdateCooperativeMutation,
  usePublishWebsiteRoutingMutation,
  useGetSubscriptionsQuery,
  useGetSubscriptionQuery,
  useCreateSubscriptionMutation,
  useUpdateSubscriptionMutation,
  useGetModulesQuery,
  useCreateModuleMutation,
  useUpdateModuleMutation,
  useGetSubModulesQuery,
  useCreateSubModuleMutation,
  useUpdateSubModuleMutation,
  useGetDashboardQuery,
  useGetCooperativeRegistrationApplicationsQuery,
  useGetDemoRequestsQuery,
  useGetContactSubmissionsQuery,
  useGetActiveCooperativesSubscriptionsQuery,
  useGetExpiringCooperativesSubscriptionsQuery,
  useGetExpiredCooperativesSubscriptionsQuery,
  useCreateCooperativeMutation,
  useUpdateCooperativeSubscriptionMutation,
  useGetCooperativeLoansQuery,
  useGetCooperativeContributionsQuery,
  useGetCooperativeTransactionsQuery,
  useGetCooperativeStatisticsQuery,
  useSendCooperativeCredentialsMutation,
  useResendWelcomeEmailMutation,
  useStartCooperativeSupportSessionMutation,
  useLazyProrateMemberSlotsQuery,
  useTopUpMemberSlotsMutation,
  useCheckDomainAvailabilityMutation,
  useCheckEmailAvailabilityMutation,
  useGetTransactionHistoryQuery,
  useGetCooperativeInvestorCapabilitiesQuery,
  useUpdateCooperativeInvestorCapabilitiesMutation,
  useGetCooperativeModuleAssignmentsQuery,
  useUpdateCooperativeModuleAssignmentsMutation,
  useResetCooperativeModuleAssignmentsMutation,
  useGetCooperativePaymentSettingsQuery,
  useUpdateCooperativePaymentSettingsMutation,
} = authApi;
